'use client'

/**
 * InteractiveTrendChart — the interactive companion to the server-rendered SVG
 * trend figure on /data/price-index (owner directive 07/20 expansion:
 * "MAX INTERACTIVITY round 2 — interactive date-range selection on the index
 * charts (presets 6m/12m/all), tooltips everywhere, YoY change").
 *
 * Built on recharts (already a dependency). It reads the SAME published trend
 * series the static figure renders, so the two can never disagree; it adds
 * client interactivity on top:
 *   • preset date ranges — 6M / 12M / All — that filter the plotted window
 *   • rich hover tooltips on every series/month
 *   • a year-over-year change row for the selected end month
 *
 * Hydration-safe: recharts measures the DOM, so the chart is rendered only after
 * mount behind a fixed-height placeholder — server and first client paint emit
 * the identical placeholder, never a mismatch. Light background, Fridge.com
 * palette, responsive at every width.
 */
import { useMemo, useState, useEffect } from 'react'
import { FigureBrandMark } from "./figure-brand-mark"
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts'
import Link from 'next/link'
import { TrendingUp, TrendingDown, Minus, ArrowUpRight } from 'lucide-react'
import { useIsHydrated } from '@/hooks/use-is-hydrated'

export interface TrendPoint {
  month: string
  index: number
}
export interface TrendSeriesInput {
  label: string
  color: string
  points: TrendPoint[]
  /** Live collection for this series (FRIDGE-334) — the legend entry links to it. */
  href?: string | null
}

type Preset = '6m' | '12m' | 'all'

function shortMonth(month: string): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  const mon = new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', {
    month: 'short',
    timeZone: 'UTC',
  })
  return `${mon} '${String(y).slice(2)}`
}

export function InteractiveTrendChart({
  series,
  baseline = 100,
  yUnit = '',
  title = 'Explore the trend',
}: {
  series: TrendSeriesInput[]
  baseline?: number
  yUnit?: string
  title?: string
}) {
  const mounted = useIsHydrated()
  const [preset, setPreset] = useState<Preset>('all')

  // Union of all months (sorted) — the full x domain.
  const allMonths = useMemo(() => {
    const s = new Set<string>()
    for (const ser of series) for (const p of ser.points) s.add(p.month)
    return [...s].sort((a, b) => a.localeCompare(b))
  }, [series])

  const windowMonths = useMemo(() => {
    if (preset === 'all') return allMonths
    const n = preset === '6m' ? 6 : 12
    return allMonths.slice(Math.max(0, allMonths.length - n))
  }, [allMonths, preset])

  // Merge into recharts rows keyed by month, restricted to the window.
  const data = useMemo(() => {
    const inWindow = new Set(windowMonths)
    const byMonth = new Map<string, Record<string, number | string>>()
    for (const m of windowMonths) byMonth.set(m, { month: m, label: shortMonth(m) })
    for (const ser of series) {
      for (const p of ser.points) {
        if (!inWindow.has(p.month)) continue
        byMonth.get(p.month)![ser.label] = p.index
      }
    }
    return windowMonths.map((m) => byMonth.get(m)!)
  }, [series, windowMonths])

  // Year-over-year change: latest month vs 12 months earlier (full series).
  const yoy = useMemo(() => {
    return series.map((ser) => {
      const pts = [...ser.points].sort((a, b) => a.month.localeCompare(b.month))
      const last = pts[pts.length - 1]
      const prior = pts.find((p) => {
        const [ly, lm] = last.month.split('-').map(Number)
        const target = `${ly - 1}-${String(lm).padStart(2, '0')}`
        return p.month === target
      })
      const delta = prior ? last.index - prior.index : null
      return { label: ser.label, color: ser.color, delta, last: last.index }
    })
  }, [series])

  const presets: Array<{ key: Preset; label: string }> = [
    { key: '6m', label: '6M' },
    { key: '12m', label: '12M' },
    { key: 'all', label: 'All' },
  ]

  const yDomain = useMemo<[number, number]>(() => {
    const vals: number[] = [baseline]
    for (const ser of series)
      for (const p of ser.points) if (windowMonths.includes(p.month)) vals.push(p.index)
    const min = Math.min(...vals)
    const max = Math.max(...vals)
    const pad = Math.max((max - min) * 0.15, 0.5)
    return [Math.floor(min - pad), Math.ceil(max + pad)]
  }, [series, windowMonths, baseline])

  return (
    <figure className="m-0 bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="px-5 sm:px-6 py-3.5 border-b border-gray-200 flex flex-col gap-y-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:flex-wrap">
        <h3 className="text-sm sm:text-[15px] font-bold tracking-tight text-gray-900 m-0">{title}</h3>
        <div className="inline-flex items-center gap-1 rounded-full border border-gray-200 p-0.5 self-start sm:self-auto" role="group" aria-label="Date range">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setPreset(p.key)}
              aria-pressed={preset === p.key}
              className={`text-[12px] font-bold px-3 h-7 rounded-full transition-colors ${
                preset === p.key ? 'bg-[#0469f0] text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-2 sm:px-4 py-4">
        <div style={{ width: '100%', height: 320 }}>
          {mounted ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 10, right: 24, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#eef1f4" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  tickLine={false}
                  axisLine={{ stroke: '#e5e7eb' }}
                  minTickGap={24}
                />
                <YAxis
                  domain={yDomain}
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  tickFormatter={(v: number) => (yUnit ? `${v}${yUnit}` : v.toFixed(1))}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: '1px solid #e5e7eb',
                    fontSize: 12,
                    boxShadow: '0 4px 14px rgba(0,0,0,0.08)',
                  }}
                  formatter={(value: number, name: string) => [
                    yUnit ? `${value}${yUnit}` : value.toFixed(2),
                    name,
                  ]}
                />
                {baseline !== undefined && (
                  <ReferenceLine
                    y={baseline}
                    stroke="#9ca3af"
                    strokeDasharray="4 4"
                    label={{ value: String(baseline), fill: '#6b7280', fontSize: 10, position: 'right' }}
                  />
                )}
                {series.map((ser) => (
                  <Line
                    key={ser.label}
                    type="monotone"
                    dataKey={ser.label}
                    stroke={ser.color}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="w-full h-full rounded-lg bg-gray-50 animate-pulse" aria-hidden="true" />
          )}
        </div>

        {/* Legend — each series links to its live collection when one exists (FRIDGE-334). */}
        <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-2 list-none p-0 m-0 px-2">
          {series.map((s) => (
            <li key={s.label} className="inline-flex items-center gap-2 text-[12px] text-gray-700">
              <span className="inline-block w-3.5 h-[3px] rounded-full" style={{ backgroundColor: s.color }} aria-hidden="true" />
              {s.href ? (
                <Link
                  href={s.href}
                  prefetch={false}
                  className="inline-flex items-center gap-1 font-semibold text-gray-900 hover:text-[#0469f0] underline decoration-[#0469f0]/40 underline-offset-[3px]"
                  title={`Browse ${s.label} on Fridge.com`}
                >
                  {s.label}
                  <ArrowUpRight className="w-3 h-3 text-[#ff9951]" aria-hidden="true" />
                </Link>
              ) : (
                s.label
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* YoY change row */}
      <figcaption className="px-5 sm:px-6 py-4 border-t border-gray-100 bg-[#0469f0]/[0.02]">
        <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-500 mb-2.5">
          Year-over-year change (latest month)
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {yoy.map((y) => {
            const up = y.delta !== null && y.delta > 0.05
            const down = y.delta !== null && y.delta < -0.05
            const Icon = up ? TrendingUp : down ? TrendingDown : Minus
            const tone = up ? 'text-[#b25410]' : down ? 'text-[#0469f0]' : 'text-gray-500'
            return (
              <div key={y.label} className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-center gap-1.5">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ backgroundColor: y.color }} />
                  <span className="text-[12px] font-semibold text-gray-700 truncate">{y.label}</span>
                </div>
                <div className={`mt-1.5 flex items-center gap-1 text-[15px] font-black ${tone}`}>
                  <Icon className="w-4 h-4" />
                  {y.delta === null ? '—' : `${y.delta > 0 ? '+' : ''}${y.delta.toFixed(1)}${yUnit || ' pts'}`}
                </div>
              </div>
            )
          })}
        </div>
        {/* FRIDGE-591: the brand mark inside the figure — screenshots carry it. */}
        <div className="mt-3 flex justify-end">
          <FigureBrandMark />
        </div>
      </figcaption>
    </figure>
  )
}
